/**
 * Impact Analysis Resource
 */

import type { ZapSEA } from '../client';
import { 
  ImpactSimulationRequest, 
  ScenarioComparisonRequest,
  JobResponse 
} from '../types';

export class ImpactResource {
  constructor(private client: ZapSEA) {}

  /**
   * Submit policy impact simulation
   * 
   * @example
   * ```typescript
   * const job = await client.impact.simulate({
   *   policyDescription: 'Federal AI regulation requiring algorithmic transparency',
   *   analysisDepth: 'comprehensive',
   *   focusAreas: ['financial_services', 'artificial_intelligence'],
   *   timeHorizon: '12_months'
   * });
   * 
   * console.log(`Job submitted: ${job.jobId}`);
   * ```
   */
  async simulate(request: ImpactSimulationRequest): Promise<JobResponse> {
    this._validateSimulationRequest(request);
    return this.client.request<JobResponse>('POST', '/v2/impact/simulate', request);
  }

  /**
   * Compare multiple policy scenarios
   * 
   * @example
   * ```typescript
   * const job = await client.impact.compareScenarios({
   *   scenarios: [
   *     {
   *       name: 'Strict Regulation',
   *       policyDescription: 'Mandatory AI audits for all financial algorithms'
   *     },
   *     {
   *       name: 'Self-Regulation',
   *       policyDescription: 'Industry-led voluntary AI transparency standards'
   *     }
   *   ],
   *   comparisonCriteria: ['implementation_cost', 'effectiveness', 'industry_acceptance']
   * });
   * ```
   */
  async compareScenarios(request: ScenarioComparisonRequest): Promise<JobResponse> {
    this._validateScenarioComparisonRequest(request);
    return this.client.request<JobResponse>('POST', '/v2/impact/compare', request);
  }

  /**
   * Get impact simulation result by job ID
   * 
   * @example
   * ```typescript
   * const result = await client.impact.getResult('job_abc123');
   * console.log(`Success probability: ${result.successProbability}`);
   * ```
   */
  async getResult(jobId: string): Promise<any> {
    if (!jobId) {
      throw new Error('Job ID is required');
    }
    return this.client.request('GET', `/v2/impact/results/${jobId}`);
  }

  /**
   * Validate impact simulation request
   */
  private _validateSimulationRequest(request: ImpactSimulationRequest): void {
    if (!request.policyDescription || typeof request.policyDescription !== 'string') {
      throw new Error('Policy description is required and must be a string');
    }

    if (request.policyDescription.trim().length < 10) {
      throw new Error('Policy description must be at least 10 characters long');
    }

    if (request.analysisDepth && !['quick', 'standard', 'comprehensive'].includes(request.analysisDepth)) {
      throw new Error('Analysis depth must be one of: quick, standard, comprehensive');
    }

    if (request.focusAreas && !Array.isArray(request.focusAreas)) {
      throw new Error('Focus areas must be an array of strings');
    }
  }

  /**
   * Validate scenario comparison request
   */
  private _validateScenarioComparisonRequest(request: ScenarioComparisonRequest): void {
    if (!request.scenarios || !Array.isArray(request.scenarios)) {
      throw new Error('Scenarios must be an array');
    }

    if (request.scenarios.length < 2) {
      throw new Error('At least 2 scenarios are required for comparison');
    }

    if (request.scenarios.length > 5) {
      throw new Error('Maximum 5 scenarios allowed for comparison');
    }

    for (const [index, scenario] of request.scenarios.entries()) {
      if (!scenario.name || typeof scenario.name !== 'string') {
        throw new Error(`Scenario ${index + 1}: name is required and must be a string`);
      }

      if (!scenario.policyDescription || typeof scenario.policyDescription !== 'string') {
        throw new Error(`Scenario ${index + 1}: policy description is required and must be a string`);
      }

      if (scenario.policyDescription.trim().length < 10) {
        throw new Error(`Scenario ${index + 1}: policy description must be at least 10 characters long`);
      }
    }

    if (request.comparisonCriteria && !Array.isArray(request.comparisonCriteria)) {
      throw new Error('Comparison criteria must be an array of strings');
    }
  }
}
